import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  AutoCompleteBoxStyled,
  AutoCompleteInputStyled,
  AutoCompleteStyled,
} from '../../asset/styled/component/AutoCompleteStyled';

import readingglasses from '../../asset/img/common/reading_glasses_icon.svg';
import downarrow from '../../asset/img/common/down_arrow_icon.svg';

const AutoComplete = React.memo(
  ({
    size = 'small',
    dataList = [],
    onSelect,
    filterFunction,
    displayProperty = 'htmComNm',
    ...props
  }) => {
    const {
      children,
      style,
      placeholder = '',
      inputStyle = { width: '350px' },
      dataBoxStyle = { width: '350px' },
      selectItem,
      isActive,
      setIsActive,
      emptySelect = false,
      ...other
    } = props;

    const firstItemRef = useRef(null);
    const containerRef = useRef(null);
    const isComposingRef = useRef(false);

    const [inputText, setInputText] = useState('');
    const [selectDataList, setSelectDataList] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    useEffect(() => {
      const handleClickOutside = event => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setIsActive(false);
          setHighlightedIndex(-1);

          if (inputText && !dataList.some(item => item[displayProperty] === inputText)) {
            onSelect(null);
          }

          setInputText('');
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [inputText, dataList, displayProperty, onSelect]);

    useEffect(() => {
      if (selectItem) {
        setInputText(selectItem);
      } else {
        setInputText('');
      }
    }, [selectItem]);

    // inputText 또는 dataList 변경 시 목록 필터링
    useEffect(() => {
      const filteredList =
        inputText === ''
          ? dataList
          : dataList.filter(item =>
              filterFunction
                ? filterFunction(item, inputText)
                : item[displayProperty].toLowerCase().includes(inputText.toLowerCase()),
            );
      setSelectDataList(filteredList);

      // 입력 텍스트가 있고 필터된 리스트가 있으면 첫 번째 항목 포커스
      if (inputText && filteredList.length > 0) {
        setHighlightedIndex(0);
      } else {
        setHighlightedIndex(-1);
      }
    }, [dataList, inputText, filterFunction, displayProperty]);

    const handleKeyNavigation = useCallback(
      e => {
        if (isComposingRef.current) {
          return;
        }

        if (isActive && selectDataList.length > 0) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            setHighlightedIndex(prevIndex => {
              const newIndex = prevIndex < selectDataList.length - 1 ? prevIndex + 1 : prevIndex;
              return newIndex;
            });
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            setHighlightedIndex(prevIndex => {
              const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
              return newIndex;
            });
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedIndex >= 0 && highlightedIndex < selectDataList.length) {
              onSelect(selectDataList[highlightedIndex]);
              if (e.target && e.target.blur) {
                e.target.blur();
              }
            }
          }
        }
      },
      [isActive, selectDataList, highlightedIndex, onSelect],
    );

    const movement = useCallback(
      (e, item) => {
        if (isActive) {
          switch (e.key) {
            case 'ArrowDown':
            case 'ArrowUp':
              e.preventDefault();
              break;
            case 'Enter':
              e.preventDefault();
              onSelect(item);
              if (e.target && e.target.blur) {
                e.target.blur();
              }
              const inputElement = containerRef.current?.querySelector('input');
              if (inputElement) {
                inputElement.blur();
              }
              setIsActive(false);
              break;
            case 'Backspace':
              if (containerRef.current) {
                const inputElement = containerRef.current.querySelector('input');
                if (inputElement) {
                  inputElement.focus();
                }
              }
              break;
            default:
              break;
          }

          switch (e.key) {
            case 'ArrowDown':
              const nextSibling = e.target.nextSibling;
              if (nextSibling) {
                nextSibling.focus();
              }
              break;
            case 'ArrowUp':
              const previousSibling = e.target.previousSibling;
              if (previousSibling) {
                previousSibling.focus();
              }
              break;
            case 'Enter':
              onSelect(item);
              if (e.target && e.target.blur) {
                e.target.blur();
              }
              const inputEl = containerRef.current?.querySelector('input');
              if (inputEl) {
                inputEl.blur();
              }
              setIsActive(false);
              break;
            default:
              break;
          }
        }
      },
      [isActive, onSelect, setIsActive],
    );

    const handleChange = useCallback((e) => {
      let newValue = e.target.value;
      setInputText(newValue);
      if (newValue === '') {
        onSelect(null);
      }
      if (emptySelect && newValue === '') {
        onSelect('');
      }
    }, [emptySelect, onSelect]);

    return (
      <AutoCompleteStyled
        ref={containerRef}
        onClick={() => setIsActive(true)}
        onFocus={() => setIsActive(true)}>
        {/* input  */}
        <AutoCompleteInputStyled {...{ size, isActive, ...other }} style={inputStyle}>
          <img src={readingglasses} alt="검색 아이콘" />
          <input
            type="text"
            className="auto_input"
            placeholder={placeholder}
            value={inputText || selectItem || ''}
            onChange={handleChange}
            ref={input => {
              if (isActive && input) {
                input.focus();
              }
            }}
            onClick={() => {
              setIsActive(true);
            }}
            onFocus={() => {
              setIsActive(true);
            }}
            onKeyDown={handleKeyNavigation}
            onCompositionStart={() => { isComposingRef.current = true; }}
            onCompositionEnd={() => { isComposingRef.current = false; }}
          />
          <img src={downarrow} alt="드롭다운 아이콘" />
        </AutoCompleteInputStyled>
        {isActive && selectDataList.length !== 0 && (
          <AutoCompleteBoxStyled style={dataBoxStyle} tabIndex="-1">
            <div className="dataBox scrollbar">
              {selectDataList.map((item, index) => (
                <div
                  key={index}
                  className="dataTextBox"
                  tabIndex="0"
                  ref={index === 0 ? firstItemRef : null}
                  onKeyDown={e => movement(e, item)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(item);
                    const inputElement = containerRef.current?.querySelector('input');
                    if (inputElement) {
                      inputElement.blur();
                    }
                    setIsActive(false);
                  }}
                  style={{
                    backgroundColor: index === highlightedIndex ? '#f5f5f5' : 'transparent',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}>
                  {item[displayProperty]}
                </div>
              ))}
            </div>
          </AutoCompleteBoxStyled>
        )}
      </AutoCompleteStyled>
    );
  },
);

export default AutoComplete;
